import { useEffect, useRef, useState } from 'react';
import { PAGE } from '../state/pages.js';
import { useAudio } from '../audio/AudioProvider.jsx';

const FLICKER_REPEAT_MS = 28000;
const FLICKER_INITIAL_DELAY_MS = 4000;

export function TitlePage({ onNavigate }) {
  const buttonRef = useRef(null);
  const mountedAtRef = useRef(Date.now());
  const [flickerActive, setFlickerActive] = useState(false);
  const { unlocked, playCue } = useAudio();

  useEffect(() => {
    buttonRef.current?.focus();
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setFlickerActive(true);
    }, FLICKER_INITIAL_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!unlocked) return undefined;
    const elapsed = Date.now() - mountedAtRef.current;
    const cycleElapsed = (elapsed - FLICKER_INITIAL_DELAY_MS) % FLICKER_REPEAT_MS;
    const remaining = elapsed < FLICKER_INITIAL_DELAY_MS
      ? FLICKER_INITIAL_DELAY_MS - elapsed
      : cycleElapsed === 0
        ? 0
        : FLICKER_REPEAT_MS - cycleElapsed;
    let interval;
    const timer = window.setTimeout(() => {
      playCue('lightningFlicker');
      interval = window.setInterval(() => {
        playCue('lightningFlicker');
      }, FLICKER_REPEAT_MS);
    }, remaining);
    return () => {
      window.clearTimeout(timer);
      if (interval) window.clearInterval(interval);
    };
  }, [playCue, unlocked]);

  function handleBegin() {
    onNavigate(PAGE.HOME);
  }

  return (
    <main
      className={
        'title-page' + (flickerActive ? ' title-page-flicker-active' : '')
      }
      aria-label="Shortfall title screen"
    >
      <div className="title-page__grid-lines" aria-hidden="true"></div>
      <div className="title-page__glow title-page__glow--orange" aria-hidden="true"></div>
      <div className="title-page__glow title-page__glow--blue" aria-hidden="true"></div>

      <svg
        className="title-page__powerline"
        viewBox="0 0 1440 220"
        preserveAspectRatio="none"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <defs>
          <filter id="title-wire-glow" x="-10%" y="-80%" width="120%" height="260%">
            <feGaussianBlur stdDeviation="2.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <g stroke="#c0c8e0" strokeWidth="2">
          <line x1="80" y1="220" x2="80" y2="100" />
          <line x1="65" y1="220" x2="95" y2="220" />
          <line x1="60" y1="160" x2="100" y2="160" />
          <line x1="55" y1="130" x2="105" y2="130" />
          <line x1="68" y1="100" x2="92" y2="100" />
          <line x1="80" y1="100" x2="60" y2="160" />
          <line x1="80" y1="100" x2="100" y2="160" />

          <line x1="380" y1="220" x2="380" y2="80" />
          <line x1="365" y1="220" x2="395" y2="220" />
          <line x1="360" y1="150" x2="400" y2="150" />
          <line x1="355" y1="115" x2="405" y2="115" />
          <line x1="368" y1="80" x2="392" y2="80" />
          <line x1="380" y1="80" x2="355" y2="150" />
          <line x1="380" y1="80" x2="405" y2="150" />

          <line x1="720" y1="220" x2="720" y2="70" />
          <line x1="705" y1="220" x2="735" y2="220" />
          <line x1="700" y1="145" x2="740" y2="145" />
          <line x1="695" y1="105" x2="745" y2="105" />
          <line x1="708" y1="70" x2="732" y2="70" />
          <line x1="720" y1="70" x2="695" y2="145" />
          <line x1="720" y1="70" x2="745" y2="145" />

          <line x1="1060" y1="220" x2="1060" y2="80" />
          <line x1="1045" y1="220" x2="1075" y2="220" />
          <line x1="1040" y1="150" x2="1080" y2="150" />
          <line x1="1035" y1="115" x2="1085" y2="115" />
          <line x1="1048" y1="80" x2="1072" y2="80" />
          <line x1="1060" y1="80" x2="1035" y2="150" />
          <line x1="1060" y1="80" x2="1085" y2="150" />

          <line x1="1380" y1="220" x2="1380" y2="100" />
          <line x1="1365" y1="220" x2="1395" y2="220" />
          <line x1="1360" y1="160" x2="1400" y2="160" />
          <line x1="1355" y1="130" x2="1405" y2="130" />
          <line x1="1368" y1="100" x2="1392" y2="100" />
          <line x1="1380" y1="100" x2="1355" y2="160" />
          <line x1="1380" y1="100" x2="1405" y2="160" />
        </g>
        <g stroke="#f97316" strokeWidth="2.5" opacity="1" filter="url(#title-wire-glow)">
          <path d="M68 100 Q230 145 368 80 Q544 30 708 70 Q884 20 1048 80 Q1214 30 1368 100" fill="none" />
          <path d="M60 130 Q230 175 355 115 Q544 60 695 105 Q880 55 1035 115 Q1210 58 1355 130" fill="none" />
          <path d="M55 160 Q225 210 360 150 Q540 95 700 145 Q878 90 1040 150 Q1210 88 1360 160" fill="none" />
        </g>
      </svg>

      <div className="title-page__status-bar" role="presentation">
        <div className="title-page__status-left">
          <div className="title-page__status-dot" aria-hidden="true"></div>
          <span className="title-page__status-label">Grid Simulator v1.0</span>
        </div>
        <span className="title-page__status-right">
          ENERGY PLANNING SYSTEM // INITIALISED
        </span>
      </div>

      {['tl', 'tr', 'bl', 'br'].map((c) => (
        <div key={c} className={`title-page__corner title-page__corner--${c}`} aria-hidden="true">
          <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 40 L0 0 L40 0" stroke="#7a82a0" strokeWidth="1.5" />
            <path d="M8 32 L8 8 L32 8" stroke="#f97316" strokeWidth="1" opacity=".5" />
          </svg>
        </div>
      ))}

      <div className="title-page__stage">
        <div className="title-page__hero">
          <h1 className="title-page__title">
            SHORTFALL
            <span className="title-page__bolt" aria-hidden="true">⚡</span>
          </h1>
          <span className="title-page__title-line" aria-hidden="true"></span>

          <p className="title-page__subtitle">
            A Least-Cost Energy Planning Game
          </p>

          <div className="title-page__begin-wrap">
            <button
              ref={buttonRef}
              type="button"
              className="title-page__begin-btn"
              onClick={handleBegin}
              data-audio-cue="buttonPrimary"
              data-testid="title-begin"
            >
              Begin
              <span className="title-page__begin-arrow" aria-hidden="true">&gt;</span>
            </button>
          </div>
        </div>
      </div>

      <div className="title-page__version" aria-hidden="true">
        BUILD v1.0.0 // SHORTFALL
      </div>
    </main>
  );
}
